import { useCallback, useEffect, useState } from "react";
import { cloudAuthDesktopV2 } from "../../services/cloudAuthDesktopV2";
import { bookChapterSync } from "../../services/bookChapterSync";
import {
  mergeChapterTimeline,
  type ChapterTimelineItem,
  type ChapterTimelineOutcome,
} from "@repo/shared/chapterHistoryTimeline";
import { Button } from "../ui/button";
import { EmptyState } from "../ui/empty-state";
import { SectionCard } from "../ui/section-card";
import { PanelHeader } from "./PanelHeader";
import { userFacingError } from "../../../shared/userFacingError";

type Props = {
  bookId: string | null | undefined;
  chapterId: string | null | undefined;
  /** When true, reload from the cloud. */
  active: boolean;
  /** After a successful restore, reload the open book from the library. */
  onRestored?: () => void | Promise<void>;
  /** Load a body kept only on this device back into the editor. */
  onRestoreLocalBody?: (input: {
    chapterId: string;
    title: string;
    contentJson: string;
  }) => void | Promise<void>;
};

function clip(text: string, max = 160): string {
  const t = text.replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function toneForOutcome(outcome: ChapterTimelineOutcome): "muted" | "default" {
  return outcome === "overwritten" || outcome === "local" ? "muted" : "default";
}

/**
 * Continuous chapter change history for the Premier SPA / desktop workbench.
 * Distinct from frozen BookVersion snapshots.
 */
export function ChapterChangeHistoryPanel({
  bookId,
  chapterId,
  active,
  onRestored,
  onRestoreLocalBody,
}: Props) {
  const [entries, setEntries] = useState<ChapterTimelineItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const bid = bookId?.trim();
    const cid = chapterId?.trim();
    if (!bid || !cid) {
      setEntries([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    // The local journal is available signed in or not — it is the only record of
    // work that never reached the server.
    const journal = await bookChapterSync.listJournal(cid).catch(() => []);
    if (!cloudAuthDesktopV2.isSignedIn()) {
      setEntries(mergeChapterTimeline([], journal, { limit: 60 }));
      setError(journal.length ? null : "Sign in to see cloud change history for this chapter.");
      setLoading(false);
      return;
    }
    try {
      const rows = await cloudAuthDesktopV2.listChapterChangeHistory(bid, cid, 40);
      setEntries(mergeChapterTimeline(rows, journal, { limit: 60 }));
    } catch (e) {
      setError(userFacingError(e, "generic"));
      setEntries(mergeChapterTimeline([], journal, { limit: 60 }));
    } finally {
      setLoading(false);
    }
  }, [bookId, chapterId]);

  const restore = useCallback(
    async (entry: ChapterTimelineItem) => {
      const bid = bookId?.trim();
      const cid = chapterId?.trim();
      if (!bid || !cid) return;
      setRestoringId(entry.id);
      setError(null);
      try {
        if (entry.source === "local") {
          if (!entry.contentJson) return;
          await onRestoreLocalBody?.({
            chapterId: cid,
            title: entry.title ?? "",
            contentJson: entry.contentJson,
          });
        } else {
          await cloudAuthDesktopV2.restoreChapterChangeHistory(
            bid,
            cid,
            entry.id.replace(/^server:/, "")
          );
          await onRestored?.();
        }
        await load();
      } catch (e) {
        setError(userFacingError(e, "generic"));
      } finally {
        setRestoringId(null);
      }
    },
    [bookId, chapterId, onRestored, onRestoreLocalBody, load]
  );

  useEffect(() => {
    if (!active) return;
    void load();
  }, [active, load]);

  return (
    <div
      className="flex h-full min-h-0 flex-col overflow-hidden"
      role="region"
      aria-label="Chapter change history"
    >
      <PanelHeader
        title="Change history"
        description="Every save and every attempt, including ones your library refused or never received."
      />

      <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto px-4 pt-3.5 pb-5">
        {loading && entries.length === 0 ? (
          <EmptyState compact title="Loading history…" />
        ) : error && entries.length === 0 ? (
          <EmptyState
            compact
            title="Couldn't load history"
            description={error}
            action={
              <Button size="sm" variant="secondary" type="button" onClick={() => void load()}>
                Retry
              </Button>
            }
          />
        ) : entries.length === 0 ? (
          <EmptyState
            title="No history yet"
            description="Saves, sync overwrites, and failed attempts appear here as you write."
          />
        ) : (
          entries.map((entry) => {
            const regions = entry.overwriteRegions ?? [];
            const meta = [
              new Date(entry.at).toLocaleString(),
              entry.userLabel,
              entry.wordCount != null ? `${entry.wordCount} words` : null,
              entry.source === "local" ? "this device" : null,
            ]
              .filter(Boolean)
              .join(" · ");

            return (
              <SectionCard
                key={entry.id}
                bodyClassName="flex flex-col gap-1.5"
                tone={toneForOutcome(entry.outcome)}
                rail={entry.outcome === "rejected" || entry.outcome === "undelivered"}
                title={entry.outcomeLabel}
              >
                <p className="text-sm leading-relaxed text-foreground">{entry.summary}</p>
                {entry.preview ? (
                  <p className="text-xs leading-relaxed text-muted-foreground">“{entry.preview}”</p>
                ) : null}
                {regions.length > 0 ? (
                  <ul className="m-0 mt-0.5 flex list-none flex-col gap-1.5 border-l-2 border-primary/35 pl-2.5">
                    {regions.slice(0, 3).map((r) => (
                      <li key={r.id} className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground/80">Discarded:</span>{" "}
                        “{clip(r.discardedText) || "(empty)"}”
                      </li>
                    ))}
                    {regions.length > 3 ? (
                      <li className="text-xs text-muted-foreground">+{regions.length - 3} more</li>
                    ) : null}
                  </ul>
                ) : null}
                <p className="text-xs text-muted-foreground">{meta}</p>
                {entry.restorable ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    type="button"
                    disabled={restoringId != null}
                    onClick={() => void restore(entry)}
                  >
                    {restoringId === entry.id
                      ? "Restoring…"
                      : entry.source === "local"
                        ? "Load this version into the editor"
                        : "Restore this version"}
                  </Button>
                ) : null}
              </SectionCard>
            );
          })
        )}
      </div>
    </div>
  );
}
